import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Autocomplete,
  Checkbox,
  CircularProgress,
  Grid,
  ListItemText,
  TextField,
} from "@mui/material";
import { Control, useController } from "react-hook-form";
import { gridSizeMdLg6, stableStringify } from "@/shared";

/**
 * Clave de la opcion "TODOS" (la agregan fetchAutocompleteBodega/Ubicacion con
 * `codigo: 'TODOS'`). Funciona como "seleccionar todo": al marcarla se pintan
 * todos los demas checks y toma el valor de la forma exclusiva.
 */
const TODOS_KEY = "TODOS";

/**
 * Autocomplete multi-select con checkboxes y scroll infinito.
 *
 * A diferencia de `CustomAutocompleteFetchForm` (single-select, valor escalar),
 * aqui el valor de RHF es un `string[]` de claves (codigos).
 *
 * Las opciones ya elegidas se guardan en un cache que no se limpia al cambiar
 * la busqueda, para que las etiquetas sobrevivan al reset del pool de opciones.
 */
export interface MultiAutocompleteCheckProps<T, P = unknown> {
  name: string;
  control: Control<any, any>;

  label?: string;
  placeholder?: string;
  disabled?: boolean;

  fetchData: (
    searchValue: string,
    page: number,
    isAll: boolean,
    keySearch: keyof T,
    params?: any
  ) => Promise<{
    options: T[];
    hasMore: boolean;
  }>;

  /** Carga una opcion puntual para poder mostrar su etiqueta (opcional). */
  loadOptionByValue?: (value: string) => Promise<T | null>;

  getOptionLabel: (option: T) => string;
  getOptionKey: (option: T) => string | number;

  size?: { xs: number; sm: number; md: number; lg: number };
  required?: boolean;

  fetchParams?: P;
  keySearch?: keyof T;
  isAll?: boolean;
}

export default function MultiAutocompleteCheckForm<T, P = unknown>(
  props: MultiAutocompleteCheckProps<T, P>
) {
  const {
    name,
    control,
    label,
    placeholder,
    disabled,
    fetchData,
    loadOptionByValue,
    getOptionLabel,
    getOptionKey,
    size = gridSizeMdLg6,
    required = false,
    fetchParams,
    keySearch = "nombre" as keyof T,
    isAll = false,
  } = props;

  const {
    field: { value, onChange, onBlur, ref },
    fieldState: { error },
  } = useController<any, typeof name>({
    name,
    control,
  });

  // --------- Refs para props inestables ---------
  const fetchDataRef = useRef(fetchData);
  const fetchParamsRef = useRef(fetchParams);
  const getOptionKeyRef = useRef(getOptionKey);
  const getOptionLabelRef = useRef(getOptionLabel);
  const loadOptionByValueRef = useRef(loadOptionByValue);

  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);
  useEffect(() => {
    fetchParamsRef.current = fetchParams;
  }, [fetchParams]);
  useEffect(() => {
    getOptionKeyRef.current = getOptionKey;
  }, [getOptionKey]);
  useEffect(() => {
    getOptionLabelRef.current = getOptionLabel;
  }, [getOptionLabel]);
  useEffect(() => {
    loadOptionByValueRef.current = loadOptionByValue;
  }, [loadOptionByValue]);

  // --------- Estados internos ---------
  const [search, setSearch] = useState<string>("");
  const [options, setOptions] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isResetting, setIsResetting] = useState(false);

  /** Cache persistente de opciones vistas: conserva etiquetas de lo ya elegido. */
  const [cache, setCache] = useState<Map<string | number, T>>(new Map());

  const searchTrimmed = search.trim();
  const isAllEffective = isAll || searchTrimmed === "";

  const selectedKeys: string[] = useMemo(
    () => (Array.isArray(value) ? value.map(String) : []),
    [value]
  );

  const keyOf = useCallback(
    (opt: T) => String(getOptionKeyRef.current(opt)),
    []
  );

  const uniqByKey = useCallback((arr: T[]): T[] => {
    const map = new Map<string | number, T>();
    for (const item of arr) map.set(getOptionKeyRef.current(item), item);
    return Array.from(map.values());
  }, []);

  /** Agrega un lote al cache de etiquetas. */
  const cachear = useCallback((lote: T[]) => {
    if (!lote.length) return;
    setCache((prev) => {
      const next = new Map(prev);
      for (const o of lote) next.set(getOptionKeyRef.current(o), o);
      return next;
    });
  }, []);

  const paramsSignature = useMemo(
    () => stableStringify(fetchParams ?? null),
    [fetchParams]
  );

  // --------- Fetch por busqueda (debounce) ---------
  const prevParamsRef = useRef(paramsSignature);

  useEffect(() => {
    const currentParams = fetchParams;
    const currentFetchData = fetchData;

    // El pool se vacia solo cuando cambian los FILTROS (p. ej. se deselige una
    // bodega). Al buscar solo por texto NO se vacia: si no, durante el debounce
    // de 400ms el desplegable parpadea a "Sin resultados" en cada tecla.
    const cambioFiltros = prevParamsRef.current !== paramsSignature;
    prevParamsRef.current = paramsSignature;

    setIsResetting(true);
    if (cambioFiltros) {
      setOptions([]);
    }
    setPage(1);
    setHasMore(true);

    let isCancelled = false;
    const t = window.setTimeout(async () => {
      setLoading(true);
      try {
        const { options: newOptions, hasMore: more } =
          await currentFetchData(
            searchTrimmed,
            1,
            isAllEffective,
            keySearch,
            currentParams
          );

        if (isCancelled) return;
        setOptions(newOptions);
        setPage(1);
        setHasMore(more);
        cachear(newOptions);
      } catch {
        if (!isCancelled) {
          setOptions([]);
          setHasMore(false);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
          setIsResetting(false);
        }
      }
    }, 400);

    return () => {
      isCancelled = true;
      window.clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cachear es estable
  }, [searchTrimmed, isAllEffective, keySearch, paramsSignature]);

  // --------- Scroll infinito ---------
  const loadMoreOptions = useCallback(async () => {
    if (isResetting || loading || !hasMore) return;

    setLoading(true);
    try {
      const next = page + 1;
      const { options: moreOpts, hasMore: more } = await fetchDataRef.current(
        searchTrimmed,
        next,
        isAllEffective,
        keySearch,
        fetchParamsRef.current
      );

      setOptions((prev) => uniqByKey([...prev, ...moreOpts]));
      setPage(next);
      setHasMore(more);
      cachear(moreOpts);
    } catch {
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [
    isResetting,
    loading,
    hasMore,
    page,
    searchTrimmed,
    isAllEffective,
    keySearch,
    uniqByKey,
    cachear,
  ]);

  // --------- Opciones visibles del desplegable ---------
  // Solo el pool devuelto para los filtros ACTUALES. El cache NO se mezcla
  // aca: hacerlo dejaba opciones obsoletas de filtros anteriores (p. ej. al
  // quitar una bodega seguian apareciendo sus ubicaciones).
  const visibleOptions = options;

  // --------- Resolver etiqueta de lo seleccionado ---------
  // El cache se usa unicamente para no perder el texto de lo ya elegido cuando
  // el pool se resetea por una nueva busqueda.
  const selectedRaw = useMemo<T[]>(() => {
    const byKey = new Map<string | number, T>();
    for (const o of visibleOptions) byKey.set(getOptionKeyRef.current(o), o);
    for (const o of cache.values()) {
      if (selectedKeys.includes(String(getOptionKeyRef.current(o)))) {
        byKey.set(getOptionKeyRef.current(o), o);
      }
    }
    return selectedKeys
      .map((k) => byKey.get(k))
      .filter((o): o is T => !!o);
  }, [selectedKeys, visibleOptions, cache]);

  // MUI borra el input cuando cambia la REFERENCIA de `value` (ver
  // useAutocomplete.js: `if (focused && !valueChange) return;` es la unica
  // proteccion, y con multiple `resetInputValue` siempre deja el input vacio).
  // Como el pool de opciones se reemplaza en cada keystroke, sin esto el
  // usuario escribe un caracter y se le borra al instante. Se reutiliza la
  // referencia anterior mientras el contenido sea equivalente.
  const selectedRef = useRef<T[]>(selectedRaw);
  const selectedArray = useMemo<T[]>(() => {
    const prev = selectedRef.current;
    const equivalente =
      prev.length === selectedRaw.length &&
      prev.every((o, i) => {
        const a = selectedRaw[i];
        return (
          String(getOptionKeyRef.current(o)) === String(getOptionKeyRef.current(a)) &&
          getOptionLabelRef.current(o) === getOptionLabelRef.current(a)
        );
      });
    if (equivalente) return prev;
    selectedRef.current = selectedRaw;
    return selectedRaw;
  }, [selectedRaw]);

  // Si quedan claves sin etiqueta (paginacion/search), se pide cada una.
  useEffect(() => {
    const loader = loadOptionByValueRef.current;
    if (typeof loader !== "function" || isResetting) return;

    const faltan = selectedKeys.filter((k) => !cache.has(k));
    if (!faltan.length) return;

    let mounted = true;
    (async () => {
      const cargadas: T[] = [];
      for (const k of faltan) {
        try {
          const o = await loader(k);
          if (o) cargadas.push(o);
        } catch {
          /* silencioso */
        }
      }
      if (!mounted || !cargadas.length) return;
      cachear(cargadas);
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cachear es estable
  }, [selectedKeys.join(","), cache, isResetting]);

  // --------- Handlers ---------
  const handleChange = useCallback(
    (_: React.SyntheticEvent, next: T[]) => {
      let claves = uniqByKey(next).map((o) =>
        String(getOptionKeyRef.current(o))
      );

      const teniaTodos = selectedKeys.includes(TODOS_KEY);
      const tieneTodosAhora = claves.includes(TODOS_KEY);

      if (tieneTodosAhora) {
        if (teniaTodos) {
          // Ya estaba activo TODOS y se marco otro elemento: TODOS deja de
          // valer (no puede ser "todos" si falta uno). MUI lo habia anexado
          // junto a TODOS, aqui se despeja para quedarse con el nuevo.
          claves = claves.filter((c) => c !== TODOS_KEY);
        } else {
          // Se acaba de marcar TODOS -> seleccion de todos, en forma exclusiva.
          claves = [TODOS_KEY];
        }
      }

      onChange(claves);
    },
    [onChange, uniqByKey, selectedKeys]
  );

  const handleInputChange = useCallback(
    (_: React.SyntheticEvent, v: string, reason: "input" | "reset" | "clear") => {
      // 'reset' NO se ignora: con multiple + clearOnBlur (default), MUI vacia el
      // input al hacer blur y lo notifica por aca. Si se ignorara, `search`
      // quedaria desincronizado del input visible -> al volver a hacer clic se
      // veia el input vacio pero con la lista aun filtrada.
      if (reason === "input" || reason === "reset") setSearch(v);
      if (reason === "clear") setSearch("");
    },
    []
  );

  return (
    <Grid item {...size}>
      <Autocomplete<T, true, false, false>
        multiple
        disableCloseOnSelect
        popupIcon={null}
        disabled={disabled}
        options={visibleOptions}
        loading={loading}
        value={selectedArray}
        onChange={handleChange}
        onInputChange={handleInputChange}
        getOptionLabel={(opt) => (opt ? getOptionLabelRef.current(opt) : "")}
        isOptionEqualToValue={(option, val) => {
          if (!option || !val) return false;
          return (
            getOptionKeyRef.current(option) === getOptionKeyRef.current(val)
          );
        }}
        filterOptions={(x) => x}
        renderOption={(props, opt) => {
          const { key: _k, ...rest } = props as any;
          // "TODOS" es la seleccion de todos: mientras este activa, TODOS los
          // elementos se pintan con check (aunque solo TODOS este en el valor).
          const todosActivo = selectedKeys.includes(TODOS_KEY);
          const marcada = todosActivo || selectedKeys.includes(keyOf(opt));
          return (
            <li {...rest} key={keyOf(opt)}>
              <Checkbox size="small" checked={marcada} />
              <ListItemText primary={getOptionLabelRef.current(opt)} />
            </li>
          );
        }}
        renderTags={() => null}
        loadingText="Cargando..."
        noOptionsText="Sin resultados"
        ListboxProps={{
          onScroll: (e: React.UIEvent<HTMLElement>) => {
            const n = e.currentTarget;
            if (n.scrollTop + n.clientHeight >= n.scrollHeight - 1) {
              loadMoreOptions();
            }
          },
          style: { maxHeight: 320, overflow: "auto" },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            required={required}
            variant="outlined"
            inputRef={ref}
            label={label}
            placeholder={placeholder}
            onBlur={onBlur}
            error={!!error}
            helperText={error?.message}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Grid>
  );
}